import React, { useState } from 'react';
import { Mail, Phone, Briefcase, MapPin, Loader } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { supabase } from '../supabaseClient';
import { CONTACT } from '../config';
import './Contact.css';

function Contact() {
  usePageTitle('Contact Us');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.inquiryType) newErrors.inquiryType = 'Please select an inquiry type';
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Message must be at least 20 characters';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setStatus('sending');
    const { error } = await supabase.from('contacts').insert([{
      name: formData.name,
      email: formData.email,
      company: formData.company,
      inquiry_type: formData.inquiryType,
      message: formData.message
    }]);
    if (error) {
      setStatus('error');
    } else {
      setStatus('success');
      setFormData({ name: '', email: '', company: '', inquiryType: '', message: '' });
      setErrors({});
    }
  };

  return (
    <div className="contact">
      <section className="contact-hero">
        <div className="contact-container">
          <h1 className="contact-hero-title">Get in Touch</h1>
          <p className="contact-hero-subtitle">Let's discuss how we can help you build intelligent software</p>
        </div>
      </section>

      <section className="contact-main">
        <div className="contact-container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Contact Information</h2>
              <div className="info-item">
                <h3><Mail size={15} style={{verticalAlign:'middle', marginRight:'0.4rem'}} />Email</h3>
                <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
              </div>
              <div className="info-item">
                <h3><Phone size={15} style={{verticalAlign:'middle', marginRight:'0.4rem'}} />Phone</h3>
                <a href={`tel:${CONTACT.phoneTel}`}>{CONTACT.phone}</a>
              </div>
              <div className="info-item">
                <h3><Briefcase size={15} style={{verticalAlign:'middle', marginRight:'0.4rem'}} />Business Hours</h3>
                <p>Monday – Friday: 9:00 AM – 6:00 PM IST</p>
              </div>
              <div className="info-item">
                <h3><MapPin size={15} style={{verticalAlign:'middle', marginRight:'0.4rem'}} />Location</h3>
                <p>Remote-first company</p>
                <p>Serving clients globally</p>
              </div>

              <div className="contact-cta-box">
                <h3>Prefer a Call?</h3>
                <p>Schedule a free 30-minute consultation to discuss your project</p>
                <a href={`tel:${CONTACT.phoneTel}`} className="btn-secondary">Call Now</a>
              </div>
            </div>

            <div className="contact-form-wrapper">
              {status === 'success' ? (
                <div className="form-success">
                  <div className="form-success-icon">✓</div>
                  <h3>Message Sent!</h3>
                  <p>Thanks for reaching out. We'll get back to you within 24 hours.</p>
                  <button className="btn-primary" onClick={() => setStatus('idle')}>Send Another Message</button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className={errors.name ? 'input-error' : ''}
                    />
                    {errors.name && <span className="field-error">{errors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className={errors.email ? 'input-error' : ''}
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Company Name</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="inquiryType">Inquiry Type *</label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                      className={errors.inquiryType ? 'input-error' : ''}
                    >
                      <option value="">Select an option</option>
                      <option value="ai-project">AI Project</option>
                      <option value="software-development">Software Development</option>
                      <option value="hire-developers">Hire Developers / Staffing</option>
                      <option value="consulting">Consulting</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.inquiryType && <span className="field-error">{errors.inquiryType}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="6"
                      placeholder="Tell us about your project or requirements... (min 20 characters)"
                      className={errors.message ? 'input-error' : ''}
                    ></textarea>
                    {errors.message && <span className="field-error">{errors.message}</span>}
                  </div>

                  <button type="submit" className="btn-primary btn-submit" disabled={status === 'sending'}>
                    {status === 'sending' ? (
                      <><Loader size={16} className="spin" /> Sending…</>
                    ) : 'Send Message'}
                  </button>
                  {status === 'error' && (
                    <p className="submit-error">Something went wrong. Please try again or email us directly.</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
